import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button, Tooltip, Dropdown } from 'antd';
import { BoldOutlined, ItalicOutlined, UnderlineOutlined, StrikethroughOutlined, BgColorsOutlined, FontSizeOutlined } from '@ant-design/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import HardBreak from '@tiptap/extension-hard-break';
import Placeholder from '@tiptap/extension-placeholder';
import History from '@tiptap/extension-history';
import '../../styles/attachment/CommentEditor.css';

let TextStyle = null;
let Color = null;
let FontSize = null;
try {
  const pkg = require('@tiptap/extension-text-style');
  TextStyle = pkg.TextStyle ?? pkg.default;
  Color = pkg.Color;
  FontSize = pkg.FontSize;
} catch {
  // @tiptap/extension-text-style not installed - basic formatting only (Bold, Italic, Underline, Strike)
}

const FONT_SIZES = ['10px', '11px', '12px', '13px', '14px'];
const PRESET_COLORS = [
  '#000000', '#262626', '#595959', '#8c8c8c', '#bfbfbf',
  '#1890ff', '#096dd9', '#0050b3', '#722ed1', '#531dab',
  '#eb2f96', '#c41d7f', '#cf1322', '#a8071a', '#d4380d',
  '#d46b08', '#ad4e00', '#389e0d', '#237804', '#52c41a',
];

const createExtensions = (placeholder, hasColor, hasFontSize) => {
  const base = [
    Document,
    Paragraph,
    Text,
    HardBreak,
    History.configure({ depth: 20 }),
    Placeholder.configure({ placeholder }),
    Bold,
    Italic,
    Underline,
    Strike,
  ];
  if (hasColor && TextStyle && Color) {
    base.push(TextStyle, Color);
  }
  if (hasFontSize && TextStyle && FontSize) {
    if (!base.includes(TextStyle)) base.push(TextStyle);
    base.push(FontSize);
  }
  return base;
};

const toEditorHtml = (val) => {
  if (!val || typeof val !== 'string') return '<p></p>';
  const t = val.trim();
  if (!t) return '<p></p>';
  if (t.startsWith('<') && (t.includes('>') || t.includes('</'))) return val;
  const escaped = t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return `<p>${escaped}</p>`;
};

const CommentEditor = forwardRef(({ value = '', onChange, placeholder = 'Add a comment...', disabled = false, toolbarPrefix = null, toolbarSuffix = null }, ref) => {
  const { t } = useTranslation();
  const isUpdatingRef = useRef(false);
  const hasColor = TextStyle && Color;
  const hasFontSize = TextStyle && FontSize;

  const editor = useEditor({
    extensions: createExtensions(placeholder, hasColor, hasFontSize),
    content: toEditorHtml(value),
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange && !isUpdatingRef.current) {
        isUpdatingRef.current = true;
        onChange(editor.getHTML());
        requestAnimationFrame(() => {
          isUpdatingRef.current = false;
        });
      }
    },
  });

  useEffect(() => {
    if (!editor || isUpdatingRef.current) return;
    const current = editor.getHTML();
    const incoming = toEditorHtml(value);
    if (value !== undefined && incoming !== current) {
      editor.commands.setContent(incoming);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
  }), [editor]);

  if (!editor) return null;

  const colorDropdownContent = (
    <div className="comment-editor-color-grid">
      {PRESET_COLORS.map((hex) => (
        <button
          key={hex}
          type="button"
          className="comment-editor-color-swatch"
          style={{ backgroundColor: hex }}
          onClick={() => editor.chain().focus().setColor(hex).run()}
        />
      ))}
    </div>
  );

  const fontSizeMenuItems = FONT_SIZES.map((size) => ({
    key: size,
    label: size,
    onClick: () => editor.chain().focus().setFontSize(size).run(),
  }));

  return (
    <div className="comment-editor">
      <div className="comment-editor-toolbar comment-editor-toolbar-banner">
        {toolbarPrefix && <div className="comment-editor-toolbar-prefix">{toolbarPrefix}</div>}
        <Tooltip title={t('messages.send.editor.bold') || 'Bold'}>
          <Button
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={disabled}
            className={editor.isActive('bold') ? 'comment-editor-btn active' : 'comment-editor-btn'}
          />
        </Tooltip>
        <Tooltip title={t('messages.send.editor.italic') || 'Italic'}>
          <Button
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={disabled}
            className={editor.isActive('italic') ? 'comment-editor-btn active' : 'comment-editor-btn'}
          />
        </Tooltip>
        <Tooltip title={t('messages.send.editor.underline') || 'Underline'}>
          <Button
            icon={<UnderlineOutlined />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={disabled}
            className={editor.isActive('underline') ? 'comment-editor-btn active' : 'comment-editor-btn'}
          />
        </Tooltip>
        <Tooltip title={t('messages.send.editor.strikethrough') || 'Strikethrough'}>
          <Button
            icon={<StrikethroughOutlined />}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={disabled}
            className={editor.isActive('strike') ? 'comment-editor-btn active' : 'comment-editor-btn'}
          />
        </Tooltip>
        {hasColor && (
          <Dropdown
            dropdownRender={() => colorDropdownContent}
            trigger={['click']}
          >
            <Tooltip title={t('attachmentPreview.textColor') || 'Text color'}>
              <Button
                icon={<BgColorsOutlined />}
                disabled={disabled}
                className={editor.isActive('textStyle') ? 'comment-editor-btn active' : 'comment-editor-btn'}
              />
            </Tooltip>
          </Dropdown>
        )}
        {hasFontSize && (
          <Dropdown
            menu={{ items: fontSizeMenuItems }}
            trigger={['click']}
          >
            <Tooltip title={t('attachmentPreview.fontSize') || 'Font size'}>
              <Button
                icon={<FontSizeOutlined />}
                disabled={disabled}
                className="comment-editor-btn"
              />
            </Tooltip>
          </Dropdown>
        )}
        {toolbarSuffix && <div className="comment-editor-toolbar-suffix">{toolbarSuffix}</div>}
      </div>
      <EditorContent editor={editor} className="comment-editor-content" />
    </div>
  );
});

CommentEditor.displayName = 'CommentEditor';

export default CommentEditor;
